import type { ChatMessage } from '../types/chat'

export type SecretaryMessageEditField = NonNullable<ChatMessage['awaitingMessageEditField']>

function foldAsciiLower(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\u0300-\u036f/g, '')
    .toLowerCase()
}

function normalizeForMatch(text: string): string {
  let t = foldAsciiLower(text || '')
  t = t.replace(/[^\w\s]/g, ' ')
  t = t.replace(/\s+/g, ' ').trim()
  return t
}

/** True when the secretary preview card should offer Edit / send Yes-No. */
export function isSecretaryPreviewActionable(m: ChatMessage): boolean {
  if (m.secretaryPreviewSuperseded || m.awaitingMessageEditField) return false
  const sent = (m.secretaryMessage?.message_sent_status || '').trim().toLowerCase()
  if (sent === 'success' || sent === 'failure') return false
  return (
    m.awaitingMessageConfirmation === true ||
    m.secretaryMessage?.autofill_note === 'awaiting_send_confirmation'
  )
}

/** User clicked Edit or said an inline-edit control like "edit subject". */
export function userTextIsSecretaryInlineEditCommand(text: string): boolean {
  const raw = (text || '').trim()
  if (!raw) return false
  const t = normalizeForMatch(raw)
  if (
    /^(?:please\s+)?(?:edit|change|modify|update)\s+(?:the\s+)?(?:purpose|subject)(?:\s+(?:to|as|with|for|into)\s+.+)?$/.test(
      t,
    )
  ) {
    return true
  }
  if (
    /^(?:please\s+)?(?:edit|change|modify|update)\s+(?:the\s+)?message(?:\s+(?:to|as|with|for|into)\s+.+)?$/.test(
      t,
    )
  ) {
    return true
  }
  if (
    /^(?:please\s+)?(?:edit|change|modify|update)\s+(?:the\s+)?recipient(?:\s+name)?(?:\s+(?:to|as|with|for|into)\s+.+)?$/.test(
      t,
    )
  ) {
    return true
  }
  if (/^(?:modifier|modifie|modifiez|corriger)\s+(?:l\s+)?objet(?:\s+(?:pour|avec|en|vers)\s+.+)?$/.test(t)) {
    return true
  }
  if (/^(?:modifier|modifie|modifiez|corriger)\s+message(?:\s+(?:pour|avec|en|vers)\s+.+)?$/.test(t)) {
    return true
  }
  if (
    /^(?:modifier|modifie|modifiez|corriger)\s+(?:le\s+)?nom\s+destinataire(?:\s+(?:pour|avec|en|vers)\s+.+)?$/.test(
      t,
    )
  ) {
    return true
  }
  return false
}

/** Any structured secretary edit command, including recipient type. */
export function userTextIsSecretaryEditCommand(text: string): boolean {
  const raw = (text || '').trim()
  if (!raw) return false
  if (userTextIsSecretaryInlineEditCommand(raw)) return true
  const t = normalizeForMatch(raw)
  if (/^edit recipient type$/.test(t)) return true
  if (/^modifier type destinataire$/.test(t)) return true
  return false
}

export function userTextIsSecretaryUrgencyCommand(text: string): boolean {
  const raw = (text || '').trim()
  if (!raw) return false
  const t = normalizeForMatch(raw)
  if (
    /^(?:please\s+)?(?:mark|flag|set)\s+(?:it|this|the\s+message)?\s*(?:as\s+)?urgent$/.test(t) ||
    /^(?:please\s+)?(?:mark|flag|set)\s+urgent$/.test(t) ||
    /^(?:please\s+)?(?:mark|flag|set)\s+(?:it|this|the\s+message)?\s*(?:as\s+)?not\s+urgent$/.test(t) ||
    /^(?:please\s+)?(?:clear|remove|unset)\s+(?:the\s+)?urgent(?:\s+flag)?$/.test(t)
  ) {
    return true
  }
  if (
    /^(?:marque|marquer|met|mettre|signale|signaler)\s+(?:ce|ceci|le\s+message)?\s*(?:comme\s+)?urgent$/.test(
      t,
    ) ||
    /^(?:marque|marquer|met|mettre)\s+(?:ce|ceci|le\s+message)?\s*(?:comme\s+)?non\s+urgent$/.test(t) ||
    /^(?:retire|retirer|enleve|enlever)\s+(?:le\s+)?marquage\s+urgent$/.test(t)
  ) {
    return true
  }
  return false
}

/** Latest assistant row is waiting for the new field value (not send confirm). */
export function conversationAwaitingSecretaryFieldEdit(messages: readonly ChatMessage[]): boolean {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.role === 'user') continue
    return Boolean(m.awaitingMessageEditField)
  }
  return false
}

/** User declined send or aborted — lock the preview card immediately. */
export function userTextIsSecretarySendDecline(text: string): boolean {
  const raw = (text || '').trim()
  if (!raw) return false
  const t = normalizeForMatch(raw)
  if (/^(no|nope|nah|non|cancel|annuler|abort|stop)$/.test(t)) return true
  if (/^no\s*,?\s*do\s+not\s+send$/.test(t) || /^no\s+do\s+not\s+send$/.test(t)) return true
  if (/^ne\s+pas\s+envoyer$/.test(t)) return true
  if (/^(cancel\s+it|annuler)$/.test(t)) return true
  return false
}

/** True if this utterance should keep secretary preview Edit / Yes / No active. */
export function userTextIsSecretaryPreviewFollowUp(
  text: string,
  messages?: readonly ChatMessage[],
): boolean {
  const raw = (text || '').trim()
  if (!raw) return false
  if (messages && conversationAwaitingSecretaryFieldEdit(messages)) return true
  if (userTextIsSecretarySendDecline(raw)) return true
  const t = normalizeForMatch(raw)

  if (/^(yes|yeah|yep|ok|okay|oui)$/.test(t)) return true
  if (/^yes\s*,?\s*send(\s+it)?$/.test(t)) return true
  if (/^oui\s*,?\s*envoyer$/.test(t)) return true

  if (userTextIsSecretaryEditCommand(raw)) return true
  if (userTextIsSecretaryUrgencyCommand(raw)) return true
  if (/\b(edit|change|modify|modifier|modifie|corriger|update)\b/i.test(raw)) return true

  return false
}
